import React from "react";
import "./styles.css";
import axios from "axios";
import {
  Button,
  Switch,
  TextField,
} from "@material-ui/core";

const EMPTY_MIN_VERSION = {
  ios: { version: "", build: 0 },
  android: { version: "", build: 0 },
  message_ro: "",
  message_en: "",
};

export default class Aplicatie extends React.Component {
  state = {
    chatEnabled: false,
    loading: true,
    saving: false,
    isVillagesClosed: true,
    isDeliveryAvailable: true,
    tipsEnabled: false,
    tipsSaving: false,
    minVersion: EMPTY_MIN_VERSION,
    minVersionSaving: false,
  };

  componentDidMount() {
    this.loadSettings();
    this.loadRestaurantAvailability();
    this.loadTipsSettings();
    this.loadMinVersion();
  }

  // "Actualizare obligatorie": builds older than this see a full-screen "update the app"
  // screen (only builds from 3.0.0 (12) check it). Super admins only.
  loadMinVersion = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get("https://api.appetito.ro/v1/admin/settings/app-version", {
        headers: { Authorization: `Bearer ${token}` },
      });
      this.setState({ minVersion: { ...EMPTY_MIN_VERSION, ...response.data } });
    } catch (e) {
      // Leave the fields empty; saving surfaces any persistent error.
    }
  };

  changeMinVersion = (platform, field, value) => {
    const minVersion = { ...this.state.minVersion };
    if (platform) {
      minVersion[platform] = { ...minVersion[platform], [field]: field === "build" ? parseInt(value, 10) || 0 : value.trim() };
    } else {
      minVersion[field] = value;
    }
    this.setState({ minVersion });
  };

  saveMinVersion = async () => {
    const { ios, android } = this.state.minVersion;
    const summary = [ios.version && `iOS ${ios.version} (${ios.build})`, android.version && `Android ${android.version} (${android.build})`]
      .filter(Boolean).join(" și ");
    if (summary && !window.confirm(`Clienții cu o versiune mai veche decât ${summary} nu vor mai putea folosi aplicația până nu o actualizează. Continui?`)) {
      return;
    }
    this.setState({ minVersionSaving: true });
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.put(
        "https://api.appetito.ro/v1/admin/settings/app-version",
        this.state.minVersion,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      this.setState({ minVersion: { ...EMPTY_MIN_VERSION, ...response.data } });
      alert("Salvat.");
    } catch (e) {
      alert(e?.response?.data?.message || "Eroare la salvarea setării.");
    } finally {
      this.setState({ minVersionSaving: false });
    }
  };

  loadTipsSettings = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get("https://api.appetito.ro/v1/admin/settings/tips", {
        headers: { Authorization: `Bearer ${token}` },
      });
      this.setState({ tipsEnabled: Boolean(response?.data?.enabled) });
    } catch (e) {
      // Leave the switch off; the toggle surfaces any persistent error.
    }
  };

  toggleTips = async () => {
    this.setState({ tipsSaving: true });
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.put(
        "https://api.appetito.ro/v1/admin/settings/tips",
        { enabled: !this.state.tipsEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      this.setState({ tipsEnabled: Boolean(response?.data?.enabled) });
    } catch (e) {
      alert("Eroare la salvarea setării.");
    } finally {
      this.setState({ tipsSaving: false });
    }
  };

  loadRestaurantAvailability = async () => {
    const token = localStorage.getItem("access_token");
    const headers = { Authorization: `Bearer ${token}` };
    const response = await axios.get("https://api.appetito.ro/v1/admin/restaurants/closed-for-village", { headers });
    const response2 = await axios.get("https://api.appetito.ro/v1/admin/restaurants/delivery-available", { headers });
    this.setState({
      isVillagesClosed: response?.data?.isClosed ?? false,
      isDeliveryAvailable: response2?.data?.available ?? true,
    });
  };

  toggleDeliveryAvailable = async () => {
    const token = localStorage.getItem("access_token");
    const response = await axios.post("https://api.appetito.ro/v1/admin/restaurants/delivery-available", {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    this.setState({ isDeliveryAvailable: response?.data?.available ?? true });
  };

  toggleVillagesClosed = async () => {
    const token = localStorage.getItem("access_token");
    const response = await axios.post("https://api.appetito.ro/v1/admin/restaurants/closed-for-village", {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    this.setState({ isVillagesClosed: response?.data?.isClosed ?? false });
  };

  loadSettings = async () => {
    this.setState({ loading: true });
    try {
      const response = await axios.get("https://api.appetito.ro/v1/app/chat/status");
      this.setState({ chatEnabled: response.data.enabled });
    } catch (e) {
      alert("Eroare la încărcarea setărilor.");
    } finally {
      this.setState({ loading: false });
    }
  };

  toggleChat = async () => {
    this.setState({ saving: true });
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        "https://api.appetito.ro/v1/admin/chat/status",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      this.setState({ chatEnabled: response.data.enabled });
    } catch (e) {
      alert("Eroare la salvarea setării.");
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const {
      chatEnabled,
      loading,
      saving,
      isVillagesClosed,
      isDeliveryAvailable,
      tipsEnabled,
      tipsSaving,
      minVersion,
      minVersionSaving,
    } = this.state;
    const superAdmin = localStorage.getItem("admin") === "true";

    if (loading) {
      return (
        <div className="settings-container">
          <div className="loader-container">
            <div className="loader" />
          </div>
        </div>
      );
    }

    return (
      <div className="settings-container">
        <h2 className="settings-title">Setări Aplicație</h2>

        <div className="settings-section">
          <h3 className="settings-section-title">Chat</h3>
          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-label">Chat activat</span>
              <span className="settings-description">
                Activează sau dezactivează funcția de chat cu asistentul Appetito din aplicație.
              </span>
            </div>
            <Switch
              checked={chatEnabled}
              onChange={this.toggleChat}
              disabled={saving}
              color="primary"
            />
          </div>
        </div>

        <div className="settings-section">
          <h3 className="settings-section-title">Restaurante</h3>
          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-label">Disponibile toate restaurantele</span>
              <span className="settings-description">
                Oprește livrarea din toate restaurantele deodată, indiferent de programul fiecăruia.
              </span>
            </div>
            <Switch
              checked={isDeliveryAvailable}
              onChange={this.toggleDeliveryAvailable}
              color="primary"
            />
          </div>
          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-label">Satele din Turda și Câmpia</span>
              <span className="settings-description">
                Activează sau dezactivează livrarea către satele din jurul Turdei și Câmpiei.
              </span>
            </div>
            <Switch
              checked={!isVillagesClosed}
              onChange={this.toggleVillagesClosed}
              color="primary"
            />
          </div>
        </div>

        <div className="settings-section">
          <h3 className="settings-section-title">Checkout</h3>
          <div className="settings-row">
            <div className="settings-row-info">
              <span className="settings-label">Bacșiș curier</span>
              <span className="settings-description">
                Permite clienților să adauge bacșiș pentru curier în checkout-ul web (doar la plata cu cardul).
                Livrarea prioritară se setează per oraș, din pagina Orașe.
              </span>
            </div>
            <Switch
              checked={tipsEnabled}
              onChange={this.toggleTips}
              disabled={tipsSaving}
              color="primary"
            />
          </div>
        </div>

        <div className="settings-section">
          <h3 className="settings-section-title">Actualizare obligatorie</h3>
          <p className="settings-description settings-paragraph">
            Clienții cu o versiune a aplicației mai veche decât cea de mai jos văd un ecran care le cere să o
            actualizeze din App Store / Google Play și nu pot folosi aplicația până atunci. O versiune e mai veche
            dacă numărul versiunii e mai mic sau, la aceeași versiune, numărul build-ului e mai mic. Lasă versiunea
            goală ca să nu ceri actualizarea. Funcționează doar pentru aplicațiile începând cu 3.0.0 (12).
          </p>
          {[["ios", "iOS"], ["android", "Android"]].map(([platform, label]) => (
            <div className="settings-row" key={platform}>
              <div className="settings-row-info">
                <span className="settings-label">{label}</span>
              </div>
              <div className="settings-version-fields">
                <TextField
                  label="Versiune minimă"
                  placeholder="ex. 3.0.1"
                  size="small"
                  variant="outlined"
                  value={minVersion[platform].version}
                  disabled={!superAdmin}
                  onChange={(e) => this.changeMinVersion(platform, "version", e.target.value)}
                />
                <TextField
                  label="Build minim"
                  type="number"
                  size="small"
                  variant="outlined"
                  value={minVersion[platform].build || ""}
                  disabled={!superAdmin}
                  onChange={(e) => this.changeMinVersion(platform, "build", e.target.value)}
                />
              </div>
            </div>
          ))}
          <div className="settings-message-fields">
            <TextField
              label="Mesaj (română, opțional)"
              placeholder="Avem o versiune nouă a aplicației. Actualizeaz-o ca să poți comanda în continuare."
              multiline
              fullWidth
              size="small"
              variant="outlined"
              value={minVersion.message_ro}
              disabled={!superAdmin}
              onChange={(e) => this.changeMinVersion(null, "message_ro", e.target.value)}
            />
            <TextField
              label="Mesaj (engleză, opțional)"
              placeholder="There's a new version of the app. Update it to keep ordering."
              multiline
              fullWidth
              size="small"
              variant="outlined"
              value={minVersion.message_en}
              disabled={!superAdmin}
              onChange={(e) => this.changeMinVersion(null, "message_en", e.target.value)}
            />
          </div>
          {superAdmin ? (
            <Button variant="contained" color="primary" onClick={this.saveMinVersion} disabled={minVersionSaving}>
              {minVersionSaving ? "Se salvează..." : "Salvează"}
            </Button>
          ) : (
            <span className="settings-description">Doar un super admin poate schimba această setare.</span>
          )}
        </div>

      </div>
    );
  }
}
